import enTranslations from "/locales/en.default.json";
import esTranslations from "/locales/es-US.json";

// Type for questionnaire translations
export type QuestionnaireTranslations = {
  next: string;
  submit: string;
  required: string;
  back: string;
  close: string;
  skip: string;
  yes: string;
  no: string;
  welcome: {
    title: string;
    description: string;
    optionSexLife: string;
    optionConfidence: string;
    optionRelationship: string;
    femaleOptionLibido?: string;
    femaleOptionConfidence?: string;
    femaleOptionSexLife?: string;
    maleOptionPerformance?: string;
    maleOptionAchievingErection?: string;
    maleOptionMaintainingErection?: string;
    existingPatient: string;
    continue: string;
    selectionRequired: string;
    goToAccount: string;
  };
  progress?: {
    stepOf: string;
    sectionComplete: string;
    sections: {
      aboutYou: string;
      healthQuestions: string;
      yourTreatment: string;
      accountSetup: string;
      checkoutDetails: string;
    };
  };
  // Add other namespaces as we create them
  intro?: {
    title: string;
    description: string;
    bulletFastTitle: string;
    bulletPrivateTitle: string;
    bulletPrivateBody: string;
    bulletNoCommitmentTitle: string;
    bulletNoCommitmentBody: string;
    clinicalReviewNote: string;
    continue: string;
  };
  healthQuestionsIntro?: {
    title: string;
    description1: string;
    description2: string;
    continue: string;
  };
  questionnaireSummary?: {
    title: string;
    description: string;
    continue: string;
  };
  stateCheck?: {
    title: string;
    description: string;
    label: string;
    placeholder: string;
    otherStateLabel: string;
    orDivider: string;
    errorPuertoRico: string;
    continue: string;
    back: string;
  };
  account?: {
    title: string;
    completeTitle: string;
    stepTitleName: string;
    stepTitleDob: string;
    stepTitleSex: string;
    stepTitleLocation: string;
    stepTitlePassword: string;
    stepTitlePasswordIntro: string;
    stepTitleIdentity: string;
    identitySubtitle: string;
    stepTitleAccess: string;
    accessSubtitle: string;
    dataProtectedReassurance: string;
    phoneReassurance: string;
    description: string;
    emailTitle: string;
    emailSubtitle: string;
    emailLabel: string;
    emailPlaceholder: string;
    emailError: string;
    emailContinueButton: string;
    emailReassurance: string;
    fieldRequired: string;
    passwordLabel: string;
    passwordPlaceholder: string;
    confirmPasswordLabel: string;
    passwordMismatchError: string;
    passwordRequiredError: string;
    sexRequiredError: string;
    dobRequiredError: string;
    tncRequiredError: string;
    tncLabel: string;
    continue: string;
    back: string;
    alreadyHaveAccount: string;
    loginLink: string;
    registerTitle: string;
    returningUserTitle: string;
    forgotPassword: string;
    loginError: string;
    loginErrorGeneric: string;
    invalidDobError: string;
    checkDetailsError: string;
    passwordBreachError: string;
    firstNameTooShortError: string;
    lastNameTooShortError: string;
    invalidEmailError: string;
    invalidPhoneError: string;
    phoneTestLineError: string;
    phoneTollFreeError: string;
    phoneTerritoryError: string;
    phoneStateMismatchError: string;
    accountCreationError: string;
    genderPurchaseMismatchError?: string;
  };
  customQuestions?: {
    medicalConditions: {
      title: string;
      label: string;
      placeholder: string;
      optionNo: string;
      optionYes: string;
    };
  };
  productSelection?: {
    doseSelection: {
      title: string;
      titleSingleVariant?: string;
      subtitle: string;
      performance: {
        title: string;
        subtitle: string;
        description: string;
      };
      performancePlus: {
        title: string;
        subtitle: string;
        description: string;
      };
      continue: string;
      selectionRequired: string;
      howToChoose: string;
      howToChooseDeso?: string;
    };
    quantitySelection: {
      title: string;
      titleSingleVariant?: string;
      subtitle: string;
      subtitleSingleVariant?: string;
      option10: string;
      option20: string;
      continue: string;
      selectionRequired: string;
      back: string;
    };
    subscriptionSelection: {
      dosage: string;
      quantity: string;
      edit: string;
      frequencyTitle: string;
      frequencySubtitle: string;
      mostPopular?: string;
      consultationFee: string;
      whatIsThis: string;
      dueToday: string;
      recurringPrice: string;
      dose: string;
      refundPolicy: string;
      taxNotice: string;
      continue: string;
      selectionRequired: string;
    };
  };
  fileUpload: {
      title: string;
      includes: string;
      fileSizeError: string;
      noFile: string;
      uploadFailed: string;
      acceptedFormats: string;
      maxFileSize: string;
      label: string;
      uploading: string;
      uploadAndContinue: string;
      back: string;
      selected: string;
      chooseFile: string;
      takePhoto: string;
      capturePhoto: string;
      cancelCamera: string;
      cameraError: string;
      retakePhoto: string;
      noFileChosen: string;
      compressionError: string;
      compressing: string;
      fileUploaded: string;
      preview: string;
      removeFile: string;
      closePreview: string;
  }
  // ... more namespaces
};

const translations = {
  en: enTranslations,
  es: esTranslations,
};

/**
 * Get all translations for a given language
 */
export function getTranslations(language: "en" | "es") {
  return translations[language] || translations.en;
}

/**
 * Get questionnaire translations for a given language
 */
export function getQuestionnaireTranslations(language: "en" | "es"): QuestionnaireTranslations {
  const lang = translations[language] || translations.en;
  return lang.questionnaire as QuestionnaireTranslations;
}

/**
 * Get translations for a specific questionnaire namespace
 */
export function getNamespaceTranslations<K extends keyof QuestionnaireTranslations>(
  language: "en" | "es",
  namespace: K
): QuestionnaireTranslations[K] {
  const questionnaire = getQuestionnaireTranslations(language);
  return questionnaire[namespace];
}